import {useEffect, useState} from 'react';
import {useSession} from '../../firebase/UserProvider';

/**
 * Shared toggle-model tag hook for the DiTag namespace (My Data segments + Highlights items).
 * No per-user votes: a tag is either linked or not. Exposes the same contract as
 * useSegmentTags ({tags, createTag, voteTag, isPosting, loadingTagOps}) so SegmentContent /
 * TagList work unchanged — voteTag acts as remove (the chip toggles the link off), createTag
 * adds a link. Tags carry no voteCount, which puts TagChip into non-vote mode.
 *
 * @param {boolean} enabled     false → all operations no-op (router hook calls this unconditionally)
 * @param {Array}   initialTags tags from the search hit ({name, color})
 * @param {Function} addFn      ({name, userId}) => Promise<updatedTagList>
 * @param {Function} removeFn   ({name, userId}) => Promise<updatedTagList>
 */
export default function useDiToggleTags({enabled, initialTags, addFn, removeFn}) {
    // Null-safe: useSession() is undefined outside UserProvider (e.g. unit tests).
    const user = useSession()?.user;
    const [tags, setTags] = useState(Array.isArray(initialTags) ? initialTags : []);
    const [isPosting, setIsPosting] = useState(false);
    const [loadingTagOps, setLoadingTagOps] = useState({});

    useEffect(() => {
        if (Array.isArray(initialTags)) setTags(initialTags);
    }, [initialTags]);

    const ready = enabled && !!user?.uid;

    async function createTag(tagName) {
        const name = (tagName || '').trim();
        if (!name || !ready) return;
        const lower = name.toLowerCase();
        if (tags.some(t => (t.name || '').toLowerCase() === lower)) return;
        setIsPosting(true);
        setTags(prev => [{name}, ...(Array.isArray(prev) ? prev : [])]);
        try {
            const updated = await addFn({name, userId: user.uid});
            if (Array.isArray(updated)) setTags(updated);
        } catch (e) {
            setTags(prev => (Array.isArray(prev) ? prev : []).filter(t => (t.name || '').toLowerCase() !== lower));
            throw e;
        } finally {
            setIsPosting(false);
        }
    }

    // Toggle: clicking a linked tag removes it; clicking an unknown tag adds it.
    async function voteTag(tagName) {
        const name = (tagName || '').trim();
        if (!name || !ready) return;
        const lower = name.toLowerCase();
        if (loadingTagOps[lower]) return;
        const isLinked = tags.some(t => (t.name || '').toLowerCase() === lower);
        if (!isLinked) return createTag(name);
        setLoadingTagOps(prev => ({...prev, [lower]: true}));
        const prevTags = Array.isArray(tags) ? tags.map(t => ({...t})) : [];
        setTags(prev => (Array.isArray(prev) ? prev : []).filter(t => (t.name || '').toLowerCase() !== lower));
        try {
            const updated = await removeFn({name, userId: user.uid});
            if (Array.isArray(updated)) setTags(updated);
        } catch (e) {
            setTags(prevTags);
            throw e;
        } finally {
            setLoadingTagOps(prev => ({...prev, [lower]: false}));
        }
    }

    return {tags, createTag, voteTag, isPosting, loadingTagOps};
}
